import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@providers/MediaQueryProvider';

import { BOARD_SOUND_THEMES } from '@exports/ChessBoard/sounds';
import { ChessGame } from '@exports/ChessGame';
import { useBoardSounds } from '@exports/hooks/useBoardSounds';
import { useBoardTheme } from '@exports/hooks/useBoardTheme';
import { IvalidPgnMoveModal } from '@exports/IvalidPgnMoveModal/IvalidPgnMoveModal';
import { PgnEditor } from '@exports/PgnEditor';
import { usePgnArrows } from '@exports/PgnEditor/hooks/usePgnArrows';
import { usePgnEditor } from '@exports/PgnEditor/hooks/usePgnEditor';
import { usePlaySequenceFen } from '@exports/PgnEditor/hooks/usePlaySequenceFen';
import { ValuatorVariant, ValuatorWidget } from '@exports/Valuator';
import { useStockfishArrows } from '@exports/Valuator/hooks/useStockfishArrows';
import { useValuatorPreview } from '@exports/Valuator/hooks/useValuatorPreview';
import { IValuationLine } from '@exports/Valuator/providers/engines';
import { AudioManager } from '@libs/audio';
import { FEN_STARTING_POSITION } from '@libs/chess/constants';
import { downloadPgn } from '@libs/chess/downloadPgn';
import { resultToWinner } from '@libs/chess/formatResults';
import { IPgnMove, Pgn } from '@libs/chess/pgn';
import { isPlayableFen } from '@libs/chess/validateFen';
import { cx } from '@libs/classnames';
import { ARRAY_EMPTY } from '@libs/constants';
import { getSessionStorage } from '@libs/storage';
import { formatSeconds } from '@libs/time';
import { BoardColorCircle } from '@ui/components/BoardColorCircle';

import { useAutoPromotion } from '@features/game/hooks/useAutoPromotion';
import {
  ButtonClose,
  ButtonDownloadPgn,
  ButtonFen,
  ButtonRotate,
  ButtonSetupPosition,
} from '@features/game/ui/GameButtons/GameButtons';
import { GameInfo } from '@features/game/ui/GameInfo/GameInfo';
import { IImportData, ImportModal } from '@features/game/ui/ImportModal/ImportModal';
import { PositionEditorModal } from '@features/game/ui/PositionEditorModal/PositionEditorModal';
import { IValidRoute } from '@features/shared/utils/router';

import { GameSelector } from './components/GamesSelector/GamesSelector';
import { Layout } from './components/Layout/Layout';

import styles from './AnalysisPage.module.css';

/*
    ____,-------------------------------,____
    \   |           Страница            |   /
    /___|-------------------------------|___\
*/

const BACK_LINK: IValidRoute = '/';

type Props = { avatar?: ReactNode };

export function AnalysisPage(props: Props) {
  const { boardTheme } = useBoardTheme();

  const [saved] = useState(() => {
    const s = PersistentState.load();
    // sic! на случай, если pgn ломает доску
    if (s) PersistentState.remove();
    if (!s) return null;

    const activePgn = Pgn.parse(s.pgn);
    if (!activePgn) return null;

    const collection = s.collection
      ?.map((str) => Pgn.parse(str))
      .filter((p): p is Pgn<IPgnMove> => p !== undefined);

    return {
      activePgn,
      collection: collection?.length ? collection : null,
      selectedGameIndex: s.selectedGameIndex ?? 0,
    };
  });

  const [data, setData] = useState(() => ({
    pgn: saved?.activePgn ?? new Pgn<IPgnMove>({}),
    ver: 1,
  }));

  const [collection, setCollection] = useState<Pgn<IPgnMove>[] | null>(saved?.collection ?? null);
  const [selectedGameIndex, setSelectedGameIndex] = useState(saved?.selectedGameIndex ?? 0);

  // HACK: передаются в Analysis вместо callback'ов
  // используется для текста в попапах и сохранения в localStorage состояния
  const boardFenRef = useRef(data.pgn.fen ?? FEN_STARTING_POSITION);
  const boardPgnRef = useRef<Pgn<IPgnMove>>(data.pgn);
  const collectionRef = useRef(collection);
  collectionRef.current = collection;
  const selectedGameIndexRef = useRef(selectedGameIndex);
  selectedGameIndexRef.current = selectedGameIndex;

  // сохранение состояние перед покиданием страницы (обычно — рефреш)
  useEffect(() => {
    const ac = new AbortController();
    window.addEventListener(
      'beforeunload',
      () => {
        const updates = boardPgnRef.current;
        const moves = updates.moves ?? [];
        const fen = data.pgn.fen ?? FEN_STARTING_POSITION;
        const hasTags = Object.keys(data.pgn.copyTags()).length > 0;
        if (moves.length === 0 && fen === FEN_STARTING_POSITION && !hasTags) {
          return;
        }

        const collection = collectionRef.current?.map((p, idx) =>
          idx === selectedGameIndexRef.current ? updates.serialize() : p.serialize(),
        );

        PersistentState.save({
          pgn: updates.serialize(),
          collection,
          selectedGameIndex: selectedGameIndexRef.current,
        });
      },
      { signal: ac.signal },
    );
    return () => ac.abort();
  }, [data]);

  const [popupMode, setPopupMode] = useState<false | true | 'fen' | 'pgn' | 'position-editor'>(
    false,
  );

  const popupInitialText = useMemo(() => {
    if (popupMode === 'fen') return boardFenRef.current;
    if (popupMode === 'pgn') {
      const updates = boardPgnRef.current;
      return updates.serialize();
    }
  }, [popupMode]);

  const onOpenPopup = useCallback((type?: 'fen' | 'pgn') => setPopupMode(type ?? true), []);
  const onClosePopup = useCallback(() => setPopupMode(false), []);
  const onImport = useCallback((data: IImportData) => {
    const pgn =
      data.pgn ??
      new Pgn<IPgnMove>({
        tags: fenToTags(data.fen),
        prelude: boardPgnRef.current.prelude,
      });

    setData((prev) => ({ ver: prev.ver + 1, pgn }));
    setPopupMode(false);
    setCollection(data.multi ?? null);
    setSelectedGameIndex(0);
    boardPgnRef.current = pgn;
  }, []);

  const onSelectGame = useCallback((pgn: Pgn<IPgnMove>, index: number) => {
    // сохраним все изменения
    const oldLive = boardPgnRef.current;

    setCollection((prev) => {
      if (!prev) return prev;
      const cur = selectedGameIndexRef.current;
      const current = prev[cur];
      if (current) {
        const updated = [...prev];
        updated[cur] = oldLive;
        return updated;
      }
      return prev;
    });

    setData((prev) => ({ ver: prev.ver + 1, pgn }));
    setSelectedGameIndex(index);
    boardPgnRef.current = pgn;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const onOpenPositionEditor = useCallback(() => setPopupMode('position-editor'), []);
  const onPositionEditorApply = useCallback(
    (fen: string) => {
      onImport({ fen });
      setCollection(null);
    },
    [onImport],
  );

  return (
    <>
      <Analysis
        key={data.ver}
        {...props}
        twoAvatars
        initialPgn={data.pgn}
        isPopupVisible={Boolean(popupMode)}
        onOpenPopup={onOpenPopup}
        onOpenPositionEditor={onOpenPositionEditor}
        boardFenRef={boardFenRef}
        boardPgnRef={boardPgnRef}
      />
      {collection && collection.length > 1 && (
        <div className={styles.games_container}>
          <GameSelector
            games={collection}
            selectedIndex={selectedGameIndex}
            theme={boardTheme}
            onSelect={onSelectGame}
          />
        </div>
      )}
      {popupMode && popupMode !== 'position-editor' && (
        <ImportModal
          className={styles.popup}
          onClose={onClosePopup}
          initialValue={popupInitialText}
          showFileUpload={popupMode === 'pgn'}
          onApply={onImport}
        />
      )}
      {popupMode === 'position-editor' && (
        <PositionEditorModal
          initialFen={boardFenRef.current}
          hideFenInput
          onClose={onClosePopup}
          onApply={onPositionEditorApply}
        />
      )}
    </>
  );
}

/*
    ____,-------------------------------,____
    \   |           Запчасти            |   /
    /___|-------------------------------|___\
*/
type PropsPage = Props & {
  isPopupVisible?: boolean;
  initialPgn: Pgn<IPgnMove>;
  initialOrientation?: 'white' | 'black';
  filename?: string;
  onOpenPopup?: (type?: 'fen' | 'pgn') => void;
  onOpenPositionEditor?: () => void;
  // HACK: для обновлений, вместо callback'ов
  boardFenRef?: React.RefObject<string>;
  boardPgnRef?: React.RefObject<Pgn<IPgnMove>>;
  //
  isRealGameAnalysis?: boolean;
  twoAvatars?: boolean;
  //
  onClose?: () => void;
};

export function Analysis({
  avatar,
  initialPgn,
  initialOrientation,
  filename,
  isPopupVisible,
  onOpenPopup,
  onOpenPositionEditor,
  boardFenRef,
  boardPgnRef,
  isRealGameAnalysis,
  twoAvatars,
  onClose,
}: PropsPage) {
  const device = useMediaQuery();

  const { boardTheme } = useBoardTheme();
  const { autoPromotion } = useAutoPromotion();

  const { boardSounds } = useBoardSounds();
  // preload sounds
  useEffect(() => {
    const resources = boardSounds && BOARD_SOUND_THEMES[boardSounds];
    if (resources) AudioManager.instance.load(resources);
  }, [boardSounds]);

  const [analyserEnabled, setAnalyserEnabled] = useState(true);
  const [valuations, setValuations] = useState<IValuationLine[]>();
  const stockfishArrows = useStockfishArrows(valuations, analyserEnabled);

  const initialFen = initialPgn.fen ?? FEN_STARTING_POSITION;
  const pgnEditor = usePgnEditor(initialPgn, {
    initialSelected: null,
    readonlyMainVariant: isRealGameAnalysis,
  });
  const { selected, pgn, playSequence, addManyMoves, addSan, updateMoveScore } = pgnEditor;
  const onMove = useCallback(
    (_m: unknown, san: string) => addSan(san, selected),
    [addSan, selected],
  );

  const display = usePlaySequenceFen(initialFen, playSequence);
  const lastMove = useMemo(
    () => (display.from && display.to ? { from: display.from, to: display.to } : undefined),
    [display.from, display.to],
  );

  if (boardFenRef) boardFenRef.current = display.fen;
  if (boardPgnRef) boardPgnRef.current = pgn;

  const playable = useMemo(() => isPlayableFen(initialFen), [initialFen]);

  const { arrows, onUserArrow } = usePgnArrows(pgnEditor, stockfishArrows);

  const handleValuation = useCallback(
    (fen: string, v: IValuationLine[]) => {
      if (fen !== display.fen) return;

      setValuations(v);

      const val = v[0];
      if (selected && val && val.depth && analyserEnabled) {
        updateMoveScore(selected.id, val.type, val.valuation);
      }
    },
    [analyserEnabled, display.fen, selected, updateMoveScore],
  );

  const onAnalyzerMoveClick = useCallback(
    (m: IPgnMove, v: ValuatorVariant) => {
      const moveSeq = v.moves.slice(0, v.moves.indexOf(m) + 1);
      addManyMoves(moveSeq, selected);

      const resources = boardSounds && BOARD_SOUND_THEMES[boardSounds];
      if (resources) {
        AudioManager.instance.load(resources).then((p) => p.move.play());
      }
    },

    [addManyMoves, boardSounds, selected],
  );

  const { preview, onMoveHover } = useValuatorPreview(display.fen);

  const handleDownloadPgn = () => {
    downloadPgn({
      filename: filename ?? `analysis-${Date.now()}.pgn`,
      winner: resultToWinner(initialPgn.result) ?? display.winner ?? null,
      link: initialPgn.link ?? 'https://play.chessopen.ru/game/analysis',
      fen: initialPgn.fen,
      event: initialPgn.event,
      site: initialPgn.site,
      round: initialPgn.round,
      white: initialPgn.white,
      black: initialPgn.black,
      studyName: initialPgn.studyName,
      chapterName: initialPgn.chapterName,
      variant: initialPgn.variant,
      moves: pgn.moves ?? [],
      prelude: pgn.prelude,
      coloredSquares: pgn.coloredSquares,
      coloredArrows: pgn.coloredArrows,
    });
  };

  const [orientation, setOrientation] = useState<'white' | 'black' | null>(
    initialOrientation ?? null,
  );
  const onToggleOrientation = () => setOrientation((o) => (o === 'black' ? 'white' : 'black'));

  const turnColor = pgnEditor.selected?.color;
  const playerBlack =
    initialPgn.black &&
    ({
      name: initialPgn.black,
      color: 'black',
      timeSec:
        turnColor === 'black' ? pgnEditor.selected?.timeRemaining : pgnEditor.prev?.timeRemaining,
    } satisfies PlayerProps);
  const playerWhite =
    initialPgn.white &&
    ({
      name: initialPgn.white,
      color: 'white',
      timeSec:
        turnColor === 'white' ? pgnEditor.selected?.timeRemaining : pgnEditor.prev?.timeRemaining,
    } satisfies PlayerProps);
  const playerTop = orientation === 'black' ? playerWhite : playerBlack;
  const playerBottom = orientation === 'black' ? playerBlack : playerWhite;

  return (
    <Layout
      disabled={isPopupVisible}
      backLink={isRealGameAnalysis ? undefined : BACK_LINK}
      backLinkIsLogo
      board={
        <ChessGame
          key={orientation}
          className={orientation ? styles.is_rotate_animated : undefined}
          fen={display.fen}
          side="all"
          lastMove={lastMove}
          autoPromotion={autoPromotion}
          boardSounds={boardSounds}
          onMove={onMove}
          orientation={orientation ?? 'white'}
          theme={boardTheme}
          notation={device !== 'desktop' ? 'inside' : undefined}
          arrows={arrows}
          onUserArrow={onUserArrow}
          noValidation
          modal={display.error ? <IvalidPgnMoveModal move={display.error.move} /> : undefined}
          onRotate={device === 'desktop' ? onToggleOrientation : undefined}
        />
      }
      avatar={avatar}
      header={
        <GameInfo
          className={cx(styles.header, {
            [styles.is_mobile]: device === 'mobile',
          })}
          mobileBackLink={isRealGameAnalysis ? undefined : BACK_LINK}
          mobileBacklinkIsLogo
          title={'Анализ партии'}
          specs={ARRAY_EMPTY}
          twoAvatars={twoAvatars}
        />
      }
      player1={playerTop && <Player {...playerTop} />}
      player2={playerBottom && <Player {...playerBottom} />}
      scoresheet={
        <PgnEditor
          className={styles.pgn}
          valuator={
            playable ? (
              <ValuatorWidget
                fen={display.fen}
                enabled={playable && analyserEnabled}
                valuateEvenIfDisabled={playable}
                preview={preview}
                onToggle={playable ? setAnalyserEnabled : undefined}
                onValuation={handleValuation}
                onMoveClick={onAnalyzerMoveClick}
                onMoveHover={onMoveHover}
              />
            ) : null
          }
          {...pgnEditor.props}
          enableGlobalShortcuts
          showPreludeEditor
        />
      }
      actions={
        <>
          {device !== 'desktop' && <ButtonRotate pill onClick={onToggleOrientation} />}
          {isRealGameAnalysis && (
            <ButtonClose onClick={onClose} pill>
              Закрыть
            </ButtonClose>
          )}
          {!isRealGameAnalysis && (
            <ButtonSetupPosition onClick={onOpenPositionEditor} pill>
              Расставить
            </ButtonSetupPosition>
          )}
          {!isRealGameAnalysis && (
            <ButtonFen onClick={() => onOpenPopup?.('fen')} pill>
              FEN
            </ButtonFen>
          )}
          {!isRealGameAnalysis && (
            <ButtonFen onClick={() => onOpenPopup?.('pgn')} pill>
              PGN
            </ButtonFen>
          )}

          <ButtonDownloadPgn
            onClick={handleDownloadPgn}
            disabled={initialFen === FEN_STARTING_POSITION && (pgn.moves?.length ?? 0) === 0}
            pill
          >
            Скачать
          </ButtonDownloadPgn>
        </>
      }
    />
  );
}

type PlayerProps = {
  name: string;
  timeSec?: number;
  color: 'black' | 'white';
};

function Player({ name, timeSec, color }: PlayerProps) {
  return (
    <div className={styles.player}>
      <span className={styles.player__name}>
        <BoardColorCircle className={styles.player__color} color={color} />
        {name}
      </span>
      <span className={styles.player__time}>
        {timeSec !== undefined && formatSeconds({ time: timeSec, showHours: timeSec >= 60 * 60 })}
      </span>
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |           Persistence         |   /
    /___|-------------------------------|___\
*/

const ANALYSIS_STATE_KEY = 'analysis-pgn-state';

type IPersistedState = {
  pgn: string;
  collection?: string[];
  selectedGameIndex?: number;
};

const PersistentState = {
  load(): IPersistedState | null {
    try {
      const raw = getSessionStorage().getItem(ANALYSIS_STATE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },
  save(state: IPersistedState) {
    try {
      getSessionStorage().setItem(ANALYSIS_STATE_KEY, JSON.stringify(state));
    } catch {
      // ignore quota
    }
  },
  remove() {
    getSessionStorage().removeItem(ANALYSIS_STATE_KEY);
  },
};

/*
    ____,-------------------------------,____
    \   |           Утилиты             |   /
    /___|-------------------------------|___\
*/

function fenToTags(fen?: string): Record<string, string> {
  if (!fen || fen === FEN_STARTING_POSITION) return {};
  return { FEN: fen };
}
